/**
 * RoomCell — One row in the open-rooms list.
 * Layout: [game logo] [stake + name title / avatar stack] [JOIN]
 *
 * Caches mini PlayerProfileBadge instances by playerId so join/leave updates
 * patch the stack without reflowing the whole row.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { PlayerProfileBadge } from '../profile/PlayerProfileBadge';
import type { LobbyPlayer, LobbyRoom } from '../../../services/LobbyService';

interface RoomCellEvents {
  'join:tap': { roomId: string };
  'profile:tap': { playerId: string };
}

const MAX_VISIBLE_AVATARS = 5;

const MODE_VISUAL: Record<LobbyRoom['mode'], { logo: string; cls: string }> = {
  ceelo: { logo: '/assets/icons/modes/game-ceelo.png', cls: 'cee' },
  craps: { logo: '/assets/icons/modes/game-craps.png', cls: 'craps' },
};

export class RoomCell extends EventEmitter<RoomCellEvents> {
  public element: HTMLElement;
  private data: LobbyRoom;
  private occupants: LobbyPlayer[];
  private stackEl: HTMLElement;
  private nameEl: HTMLElement;
  private stakeEl: HTMLElement;
  private ctaEl: HTMLElement;
  private iconEl: HTMLElement;
  private logoEl: HTMLImageElement;
  private tagEl: HTMLElement;
  private miniBadges: Map<string, PlayerProfileBadge> = new Map();

  constructor(data: LobbyRoom, occupants: LobbyPlayer[]) {
    super();
    RoomCell.injectStyles();

    this.data = data;
    this.occupants = occupants;

    this.element = document.createElement('div');
    this.element.className = 'sdl-room';
    this.element.dataset.roomId = data.id;
    this.element.innerHTML = `
      <div class="sdl-room-icon" data-icon><img class="sdl-room-logo" data-logo alt="" draggable="false"></div>
      <div class="sdl-room-body">
        <div class="sdl-room-top">
          <span class="sdl-room-stake" data-stake></span>
          <div class="sdl-room-name" data-name></div>
          <div class="sdl-room-tag" data-tag></div>
        </div>
        <div class="sdl-room-stack" data-stack-list></div>
      </div>
      <button class="sdl-room-cta" data-cta type="button">JOIN</button>
    `;

    this.iconEl = this.element.querySelector('[data-icon]') as HTMLElement;
    this.logoEl = this.element.querySelector('[data-logo]') as HTMLImageElement;
    this.nameEl = this.element.querySelector('[data-name]') as HTMLElement;
    this.tagEl = this.element.querySelector('[data-tag]') as HTMLElement;
    this.stakeEl = this.element.querySelector('[data-stake]') as HTMLElement;
    this.stackEl = this.element.querySelector('[data-stack-list]') as HTMLElement;
    this.ctaEl = this.element.querySelector('[data-cta]') as HTMLElement;

    // Tap handling: differentiate JOIN tap, mini-avatar tap, vs whole-cell tap.
    this.element.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const miniBadgeEl = target.closest('[data-player-id]') as HTMLElement | null;
      if (miniBadgeEl) {
        e.stopPropagation();
        const playerId = miniBadgeEl.dataset.playerId!;
        this.emit('profile:tap', { playerId });
        return;
      }
      // Tapping CTA or anywhere else fires join (FULL is handled by isJoinable())
      if (this.isJoinable()) {
        this.emit('join:tap', { roomId: this.data.id });
      }
    });

    this.render();
  }

  update(data: LobbyRoom, occupants: LobbyPlayer[]): void {
    this.data = data;
    this.occupants = occupants;
    this.render();
  }

  getData(): LobbyRoom {
    return this.data;
  }

  destroy(): void {
    this.miniBadges.forEach((b) => b.destroy());
    this.miniBadges.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  // ---------- Rendering ----------

  private render(): void {
    const { name, mode, minStake, private: isPrivate, maxPlayers, playerIds } = this.data;
    const visual = MODE_VISUAL[mode];

    // Game logo
    this.iconEl.className = `sdl-room-icon sdl-room-icon-${visual.cls}`;
    if (this.logoEl.getAttribute('src') !== visual.logo) {
      this.logoEl.src = visual.logo;
    }

    // Title row: buy-in amount + table name
    this.stakeEl.textContent = `💵 ${formatStacks(minStake)}`;
    this.nameEl.textContent = name;
    if (isPrivate) {
      this.tagEl.style.display = '';
      this.tagEl.textContent = '🔒';
    } else {
      this.tagEl.style.display = 'none';
      this.tagEl.textContent = '';
    }

    // Avatar stack
    this.renderStack(playerIds, maxPlayers);

    // CTA
    const joinable = this.isJoinable();
    this.ctaEl.textContent = joinable ? 'JOIN' : 'FULL';
    this.ctaEl.classList.toggle('sdl-room-cta-full', !joinable);
  }

  private renderStack(playerIds: string[], maxPlayers: number): void {
    const occupantById = new Map(this.occupants.map((p) => [p.id, p]));
    const filledIds = playerIds.slice(0, MAX_VISIBLE_AVATARS);
    const seen = new Set<string>();

    // Reuse / create badges for filled slots, in order.
    const fragment = document.createDocumentFragment();
    let renderedCount = 0;
    filledIds.forEach((id) => {
      seen.add(id);
      const player = occupantById.get(id);
      if (!player) return;
      let badge = this.miniBadges.get(id);
      if (badge) {
        badge.update({
          username: player.username,
          ringColor: player.ringColor,
          picture: player.picture,
        });
      } else {
        badge = new PlayerProfileBadge(
          {
            id: player.id,
            username: player.username,
            ringColor: player.ringColor,
            picture: player.picture,
          },
          'mini'
        );
        this.miniBadges.set(id, badge);
      }
      fragment.appendChild(badge.element);
      renderedCount++;
    });

    // Always show a full row of MAX_VISIBLE_AVATARS slots — fill the rest with
    // empty placeholders. Counting rendered avatars (not raw ids) keeps the row
    // at MAX even if an occupant ever fails to resolve. (maxPlayers is intentionally
    // not a cap here: the row is a fixed-size showcase, not a literal seat map.)
    void maxPlayers;
    const remaining = MAX_VISIBLE_AVATARS - renderedCount;
    for (let i = 0; i < remaining; i++) {
      fragment.appendChild(PlayerProfileBadge.createEmptySlot());
    }

    // Drop badges no longer in stack
    for (const [id, badge] of this.miniBadges) {
      if (!seen.has(id)) {
        badge.destroy();
        this.miniBadges.delete(id);
      }
    }

    this.stackEl.innerHTML = '';
    this.stackEl.appendChild(fragment);
  }

  private isJoinable(): boolean {
    return this.data.playerIds.length < this.data.maxPlayers;
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('sdl-room-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-room-styles';
    style.textContent = `
      .sdl-room {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 14px;
        cursor: pointer;
        transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
      }
      .sdl-room:active {
        transform: scale(0.98);
        background: var(--concrete-lighter);
      }
      .sdl-room-featured {
        border-color: rgba(255, 0, 128, 0.25);
        background: linear-gradient(135deg, rgba(255,0,128,0.08), var(--concrete-light) 70%);
      }

      .sdl-room-icon {
        width: 56px; height: 56px;
        flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: var(--concrete);
        border: 1px solid var(--glass-border);
        border-radius: 12px;
        overflow: hidden;
      }
      .sdl-room-logo {
        width: 100%; height: 100%;
        object-fit: contain;
        padding: 6px;
        box-sizing: border-box;
      }
      .sdl-room-icon-cee   { background: linear-gradient(135deg, rgba(143,201,58,0.15), var(--concrete)); border-color: rgba(143,201,58,0.3); }
      .sdl-room-icon-craps { background: linear-gradient(135deg, rgba(233,30,75,0.15), var(--concrete)); border-color: rgba(233,30,75,0.3); }

      .sdl-room-body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .sdl-room-top {
        display: flex; align-items: center; gap: 8px;
        min-width: 0;
      }
      .sdl-room-stake {
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 0.5px;
        color: var(--green);
        white-space: nowrap;
        flex-shrink: 0;
      }
      .sdl-room-name {
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 1px;
        color: rgba(255,255,255,0.65);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
        flex: 1;
      }
      .sdl-room-tag {
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 1.2px;
        padding: 2px 6px;
        border-radius: 4px;
        background: rgba(255,255,255,0.06);
        color: rgba(255,255,255,0.5);
        border: 1px solid rgba(255,255,255,0.1);
        flex-shrink: 0;
      }

      .sdl-room-stack {
        display: flex;
        align-items: center;
        gap: 6px;
        min-height: 32px;
      }
      /* Avatars get extra presence in the cell — bigger than the default mini badge,
         spaced apart rather than overlapping now that the row has room. */
      .sdl-room-stack .sdl-pbadge-mini { width: 32px; height: 32px; }

      .sdl-room-cta {
        all: unset;
        flex-shrink: 0;
        margin-left: 10px;
        font-family: var(--font-display);
        font-size: 13px;
        letter-spacing: 1.5px;
        padding: 8px 14px;
        border-radius: 8px;
        background: rgba(255,215,0,0.12);
        color: var(--gold);
        border: 1px solid rgba(255,215,0,0.3);
        cursor: pointer;
        text-align: center;
      }
      .sdl-room:active .sdl-room-cta { background: rgba(255,215,0,0.2); }
      .sdl-room-cta-full {
        background: rgba(255,255,255,0.04) !important;
        color: rgba(255,255,255,0.35) !important;
        border-color: rgba(255,255,255,0.08) !important;
        cursor: default;
      }
    `;
    document.head.appendChild(style);
  }
}

function formatStacks(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 ? 1 : 0)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return `${value}`;
}
